import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import DealRadarCard from '../components/DealRadarCard';
import { api } from '../lib/api';
import type { Property, PropertiesResponse } from '../types';
import { Button, SearchInput, Select, Input, PropertyCard, PropertyCardCompact, PropertyCardSkeleton, StatsSkeleton, Dialog } from '../components/ui';
import { PortfolioCharts } from '../components/charts';
import PropertyComparison from '../components/PropertyComparison';
import DuplicatePropertyDialog from '../components/DuplicatePropertyDialog';
import AddPropertyChoices from '../components/AddPropertyChoices';
import {
  duplicateCandidatesFrom,
  friendlyErrorMessage,
  propertyIdFrom,
  DuplicateCandidate,
  DuplicateAction,
  createSubmitLock,
} from '../utils/duplicateChoice';

type ViewMode = 'grid' | 'list' | 'analytics';
type SortOption = 'newest' | 'price-high' | 'price-low' | 'cap-rate' | 'size';

export default function Dashboard() {
  const navigate = useNavigate();
  const [properties, setProperties] = useState<Property[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState<SortOption>('newest');
  const [propertyTypeFilter, setPropertyTypeFilter] = useState('');
  const [viewMode, setViewMode] = useState<ViewMode>('grid');
  const [showComparison, setShowComparison] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [addMode, setAddMode] = useState<'choose' | 'manual'>('choose');
  const [addSaving, setAddSaving] = useState(false);
  const [addError, setAddError] = useState('');
  // A5. The form is uncontrolled (read once via FormData), so the body that was rejected as a
  // duplicate has to be kept here - "Create separate" resubmits exactly it, plus force_create.
  // Re-reading the form later is not an option: the modal stays open behind the dialog, but
  // nothing guarantees the DOM node is still around by the time the user answers.
  const [duplicates, setDuplicates] = useState<DuplicateCandidate[] | null>(null);
  const [pendingBody, setPendingBody] = useState<Record<string, unknown> | null>(null);
  // A5. Single-flight lock for the duplicate dialog's actions, acquired synchronously.
  // `busy` state cannot do this job: React batches updates, so two clicks in the same tick
  // both see it as false. See createSubmitLock's comment - this created two real rows.
  const submitLock = useRef(createSubmitLock());

  // Extracted from the mount effect so a create that succeeded but answered an unrecognised
  // shape can still put the new property in front of the user (see handleCreated below).
  const loadProperties = useCallback(async () => {
    try {
      const data = await api.listProperties();
      const response = data as PropertiesResponse;
      setProperties(response.properties || []);
    } catch (err) {
      setError(friendlyErrorMessage(err, 'Failed to load properties'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadProperties();
  }, [loadProperties]);

  /**
   * What to do with a create response. Called only after the request RESOLVED, so the property
   * exists no matter what comes back.
   *
   * `propertyIdFrom` returning null used to be an unguarded `result.property.id`, which threw
   * into the catch and showed "Failed to create property" for a property that was created. The
   * user's response to that message is to retry - which makes the duplicate this whole feature
   * exists to prevent. Refreshing the list is the honest fallback: the new row shows up.
   */
  const handleCreated = (result: unknown) => {
    setDuplicates(null);
    setShowAddModal(false);
    const id = propertyIdFrom(result);
    if (id) {
      navigate(`/properties/${id}`);
    } else {
      loadProperties();
    }
  };

  const handleManualSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setAddSaving(true);
    setAddError('');

    const fd = new FormData(e.currentTarget);
    const body: Record<string, unknown> = {
      address: fd.get('address') as string,
      city: fd.get('city') as string,
      state: (fd.get('state') as string) || 'CA',
      zip_code: fd.get('zip_code') as string || undefined,
      property_type: fd.get('property_type') as string || undefined,
      building_size: fd.get('building_size') ? Number(fd.get('building_size')) : undefined,
      lot_size: fd.get('lot_size') ? Number(fd.get('lot_size')) : undefined,
      year_built: fd.get('year_built') ? Number(fd.get('year_built')) : undefined,
      price: fd.get('price') ? Number(fd.get('price')) : undefined,
      cap_rate: fd.get('cap_rate') ? Number(fd.get('cap_rate')) : undefined,
      apn: fd.get('apn') as string || undefined,
    };
    // Remove undefined values
    Object.keys(body).forEach((k) => { if (body[k] === undefined || body[k] === '') delete body[k]; });

    try {
      handleCreated(await api.createProperty(body));
    } catch (err: any) {
      // A 409 means the server declined to insert a second row for a building we already
      // have. Everything else - including the 503 that means the duplicate CHECK itself is
      // broken - stays an ordinary error and never reaches the "create separate" button.
      const candidates = duplicateCandidatesFrom(err);
      if (candidates) {
        setDuplicates(candidates);
        setPendingBody(body);
        return;
      }
      setAddError(friendlyErrorMessage(err, 'Failed to create property'));
    } finally {
      setAddSaving(false);
    }
  };

  /**
   * The answer to the duplicate prompt (A5). No document exists on this path, so
   * `availableActions` offers only Open and Create separate - there is nothing to attach.
   *
   * `force_create` is set here and in no other place on this page.
   */
  const handleDuplicateChoice = async (action: DuplicateAction, c: DuplicateCandidate) => {
    if (action === 'open') {
      setDuplicates(null);
      setShowAddModal(false);
      navigate(`/properties/${c.id}`);
      return;
    }
    // Defensive: 'attach' cannot be offered without a documentId, and this page has none.
    if (action !== 'create_separate' || !pendingBody) return;

    // ⚠️ Acquired SYNCHRONOUSLY, before any await. `addSaving` disables the button, but it is
    // React state and React batches updates - two clicks in the same tick both read it as
    // false and both create a property. That is not hypothetical: it created two rows 2ms
    // apart against production on 2026-08-06.
    if (!submitLock.current.tryAcquire()) return;
    setAddSaving(true);
    setAddError('');
    try {
      handleCreated(await api.createProperty({ ...pendingBody, force_create: true }));
    } catch (err: any) {
      setDuplicates(null);
      setAddError(friendlyErrorMessage(err, 'Failed to create property'));
    } finally {
      submitLock.current.release();
      setAddSaving(false);
    }
  };

  // Calculate portfolio stats with health metrics
  const stats = useMemo(() => {
    if (properties.length === 0) {
      return {
        totalProperties: 0,
        totalValue: 0,
        avgCapRate: 0,
        totalSqft: 0,
        avgPriceSf: 0,
        portfolioHealth: 0,
        diversificationScore: 0,
        propertiesWithComps: 0,
        propertiesAnalyzed: 0,
      };
    }

    const totalValue = properties.reduce((sum, p) => sum + (p.price || 0), 0);
    const capRates = properties.filter((p) => p.cap_rate).map((p) => p.cap_rate!);
    const avgCapRate = capRates.length > 0
      ? capRates.reduce((sum, rate) => sum + rate, 0) / capRates.length
      : 0;
    const totalSqft = properties.reduce((sum, p) => sum + (p.building_size || 0), 0);
    const avgPriceSf = totalSqft > 0 ? totalValue / totalSqft : 0;

    // Calculate portfolio health metrics
    const uniqueTypes = new Set(properties.map(p => p.property_type).filter(Boolean));
    const uniqueMarkets = new Set(properties.map(p => p.market || p.city).filter(Boolean));

    // Diversification score: based on property types and markets
    const typeDiversity = Math.min((uniqueTypes.size / 5) * 50, 50); // Max 50 points for 5+ types
    const marketDiversity = Math.min((uniqueMarkets.size / 3) * 50, 50); // Max 50 points for 3+ markets
    const diversificationScore = typeDiversity + marketDiversity;

    // Data completeness score
    const propertiesWithPrice = properties.filter(p => p.price).length;
    const propertiesWithSize = properties.filter(p => p.building_size).length;
    const propertiesWithCapRate = properties.filter(p => p.cap_rate).length;
    const dataCompleteness = ((propertiesWithPrice + propertiesWithSize + propertiesWithCapRate) / (properties.length * 3)) * 100;

    // Overall portfolio health (weighted average)
    const portfolioHealth = Math.round((diversificationScore * 0.4) + (dataCompleteness * 0.6));

    return {
      totalProperties: properties.length,
      totalValue,
      avgCapRate,
      totalSqft,
      avgPriceSf,
      portfolioHealth,
      diversificationScore: Math.round(diversificationScore),
      propertiesWithComps: propertiesWithCapRate, // Using cap rate as proxy for analyzed
      propertiesAnalyzed: propertiesWithCapRate,
    };
  }, [properties]);

  // Get unique property types for filter
  const propertyTypes = useMemo(() => {
    const types = new Set(properties.map((p) => p.property_type).filter(Boolean));
    return Array.from(types).map((type) => ({
      value: type!,
      label: type!.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()),
    }));
  }, [properties]);

  // Filter and sort properties
  const filteredProperties = useMemo(() => {
    let result = [...properties];

    // Search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      result = result.filter(
        (p) =>
          p.address?.toLowerCase().includes(query) ||
          p.city?.toLowerCase().includes(query) ||
          p.state?.toLowerCase().includes(query) ||
          p.market?.toLowerCase().includes(query)
      );
    }

    // Property type filter
    if (propertyTypeFilter) {
      result = result.filter((p) => p.property_type === propertyTypeFilter);
    }

    // Sort
    switch (sortBy) {
      case 'price-high':
        result.sort((a, b) => (b.price || 0) - (a.price || 0));
        break;
      case 'price-low':
        result.sort((a, b) => (a.price || 0) - (b.price || 0));
        break;
      case 'cap-rate':
        result.sort((a, b) => (b.cap_rate || 0) - (a.cap_rate || 0));
        break;
      case 'size':
        result.sort((a, b) => (b.building_size || 0) - (a.building_size || 0));
        break;
      case 'newest':
      default:
        result.sort((a, b) =>
          new Date(b.created_at || 0).getTime() - new Date(a.created_at || 0).getTime()
        );
    }

    return result;
  }, [properties, searchQuery, propertyTypeFilter, sortBy]);

  const formatCurrency = (value: number) => {
    if (value >= 1000000) {
      return `$${(value / 1000000).toFixed(1)}M`;
    }
    return `$${value.toLocaleString()}`;
  };

  const formatNumber = (value: number) => {
    if (value >= 1000000) {
      return `${(value / 1000000).toFixed(1)}M`;
    }
    if (value >= 1000) {
      return `${(value / 1000).toFixed(0)}K`;
    }
    return value.toLocaleString();
  };

  return (
    <Layout>
      <div className="space-y-6">
        {/* Page Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="font-display text-[32px] leading-[1.06] font-semibold uppercase tracking-[-.01em] text-gray-900">My Deals</h1>
            <p className="text-gray-500 mt-1">
              Manage and analyze your commercial real estate deals
            </p>
          </div>
          {/* 🚨 `flex-wrap`: three buttons at `gap-3` are 449px wide and a phone is 393. Without it
              "Add Property" hung 56px off the right edge and the whole page scrolled sideways -
              measured on a Pixel 3a. Same fix OO4 made to the deal header's action row. */}
          <div className="flex flex-wrap gap-3">
            <Button
              variant="outline"
              onClick={() => navigate('/data-hub')}
              leftIcon={
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 7v10c0 2.21 3.582 4 8 4s8-1.79 8-4V7M4 7c0 2.21 3.582 4 8 4s8-1.79 8-4M4 7c0-2.21 3.582-4 8-4s8 1.79 8 4m0 5c0 2.21-3.582 4-8 4s-8-1.79-8-4" />
                </svg>
              }
            >
              Property Database
            </Button>
            {properties.length >= 2 && (
              <Button
                variant="outline"
                onClick={() => setShowComparison(true)}
                leftIcon={
                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                  </svg>
                }
              >
                Compare
              </Button>
            )}
            <Button
              onClick={() => { setShowAddModal(true); setAddMode('choose'); setAddError(''); }}
              leftIcon={
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
              }
            >
              Add Property
            </Button>
          </div>
        </div>

        {/* Error Alert */}
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg flex items-center">
            <svg className="w-5 h-5 mr-2 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            {error}
          </div>
        )}

        {/* Stats Cards */}
        {loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <StatsSkeleton />
            <StatsSkeleton />
            <StatsSkeleton />
            <StatsSkeleton />
          </div>
        ) : properties.length > 0 ? (
          /* Key metrics — clean bordered strip (hairlines via gap on a gray ground) */
          <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-px rounded-xl border border-gray-200 bg-gray-200 shadow-sm overflow-hidden">
            {[
              { k: 'Properties', v: String(stats.totalProperties) },
              { k: 'Portfolio Value', v: formatCurrency(stats.totalValue), accent: true },
              { k: 'Avg Cap Rate', v: stats.avgCapRate > 0 ? `${stats.avgCapRate.toFixed(2)}%` : '—' },
              { k: 'Total SF', v: `${formatNumber(stats.totalSqft)} SF` },
              { k: 'Avg $/SF', v: stats.avgPriceSf > 0 ? `$${stats.avgPriceSf.toFixed(0)}` : '—' },
              { k: 'Analyzed', v: `${stats.propertiesAnalyzed}/${stats.totalProperties}` },
              { k: 'Diversification', v: `${stats.diversificationScore}%` },
              {
                k: 'Health Score',
                v: `${stats.portfolioHealth}/100`,
                sub: stats.portfolioHealth >= 70 ? 'Excellent' : stats.portfolioHealth >= 40 ? 'Good' : 'Needs attention',
              },
            ].map((m, i) => (
              <div key={i} className="bg-white px-4 py-3.5">
                <p className="text-[10.5px] uppercase tracking-wider text-gray-400 mb-1.5">{m.k}</p>
                {/* Prices are charcoal, never red (the design); the number in the metric-strip's Oswald */}
                <p className="font-display text-[19px] font-medium leading-none tabular-nums text-gray-900">{m.v}</p>
                {(m as any).sub && <p className="text-[10.5px] text-gray-400 mt-0.5">{(m as any).sub}</p>}
              </div>
            ))}
          </div>
        ) : null}

        {/* Deal Radar — on-demand AI signal sweep (same engine as the 7am email) */}
        <DealRadarCard />

        {/* Filters & Search */}
        {!loading && properties.length > 0 && (
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1">
              <SearchInput
                placeholder="Search by address, city, or market..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onClear={() => setSearchQuery('')}
              />
            </div>
            <div className="flex gap-3">
              <Select
                options={[
                  { value: '', label: 'All Types' },
                  ...propertyTypes,
                ]}
                value={propertyTypeFilter}
                onChange={(e) => setPropertyTypeFilter(e.target.value)}
                className="w-40"
              />
              <Select
                options={[
                  { value: 'newest', label: 'Newest First' },
                  { value: 'price-high', label: 'Price: High to Low' },
                  { value: 'price-low', label: 'Price: Low to High' },
                  { value: 'cap-rate', label: 'CAP Rate' },
                  { value: 'size', label: 'Size' },
                ]}
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value as SortOption)}
                className="w-44"
              />
              {/* View Toggle */}
              <div className="hidden sm:flex border border-gray-200 rounded-lg overflow-hidden divide-x divide-gray-200">
                <button
                  onClick={() => setViewMode('grid')}
                  className={`p-2 transition-colors ${viewMode === 'grid' ? 'bg-gray-100 text-gray-900' : 'bg-white text-gray-400 hover:text-gray-600 hover:bg-gray-50'}`}
                  title="Grid view"
                >
                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
                  </svg>
                </button>
                <button
                  onClick={() => setViewMode('list')}
                  className={`p-2 transition-colors ${viewMode === 'list' ? 'bg-gray-100 text-gray-900' : 'bg-white text-gray-400 hover:text-gray-600 hover:bg-gray-50'}`}
                  title="List view"
                >
                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 10h16M4 14h16M4 18h16" />
                  </svg>
                </button>
                <button
                  onClick={() => setViewMode('analytics')}
                  className={`p-2 transition-colors ${viewMode === 'analytics' ? 'bg-gray-100 text-gray-900' : 'bg-white text-gray-400 hover:text-gray-600 hover:bg-gray-50'}`}
                  title="Analytics view"
                >
                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                  </svg>
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Results Count */}
        {!loading && properties.length > 0 && (
          <div className="flex items-center justify-between">
            <span className="text-[11px] uppercase tracking-wider text-gray-400">
              Showing {filteredProperties.length} of {properties.length} properties
            </span>
          </div>
        )}

        {/* Property Grid/List */}
        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            <PropertyCardSkeleton />
            <PropertyCardSkeleton />
            <PropertyCardSkeleton />
            <PropertyCardSkeleton />
            <PropertyCardSkeleton />
            <PropertyCardSkeleton />
          </div>
        ) : properties.length === 0 ? (
          <p className="text-xs text-gray-400 py-1">
            No properties yet —{' '}
            <button onClick={() => navigate('/upload')} className="text-primary-500 hover:underline">
              upload your first property
            </button>{' '}
            and we'll extract the key information for you.
          </p>
        ) : filteredProperties.length === 0 ? (
          <p className="text-xs text-gray-400 py-1">
            No matching properties —{' '}
            <button
              onClick={() => {
                setSearchQuery('');
                setPropertyTypeFilter('');
              }}
              className="text-primary-500 hover:underline"
            >
              clear filters
            </button>{' '}
            to see everything.
          </p>
        ) : viewMode === 'analytics' ? (
          <PortfolioCharts properties={properties} />
        ) : viewMode === 'grid' ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {filteredProperties.map((property) => (
              <PropertyCard key={property.id} property={property} />
            ))}
          </div>
        ) : (
          <div className="space-y-3">
            {filteredProperties.map((property) => (
              <PropertyCardCompact key={property.id} property={property} />
            ))}
          </div>
        )}
      </div>

      {/* Property Comparison Modal */}
      {showComparison && (
        <PropertyComparison
          properties={properties}
          onClose={() => setShowComparison(false)}
        />
      )}

      {/* Add Property Modal - OO5d: through the Dialog primitive. ⛔ It stays at the DEFAULT z-50
          so the A5 duplicate prompt below (z-60) keeps sitting on top of it with this form still
          open underneath. The "choose" step has no action row - its two options ARE the actions. */}
      <Dialog
        open={showAddModal}
        onClose={() => setShowAddModal(false)}
        title={addMode === 'choose' ? 'Add Property' : 'New Property'}
        /* UI-GLASS1 (the 132nd): the choose step offers a short list of choices, so it is iOS 26's menu - a frosted
           panel with pill rows. ⛔ The form after it is a form: the card look, as before. */
        look={addMode === 'choose' ? 'choices' : 'card'}
        size={addMode === 'choose' ? 'sm' : 'lg'}
        footer={addMode === 'choose' ? undefined : (<>
          <Button type="button" variant="outline" onClick={() => setAddMode('choose')} className="flex-1">
            Back
          </Button>
          <Button type="submit" form="dashboard-add-property" isLoading={addSaving} disabled={addSaving} className="flex-1">
            {addSaving ? 'Creating...' : 'Create Property'}
          </Button>
        </>)}
      >
              {addMode === 'choose' ? (
                <AddPropertyChoices
                  onUpload={() => { setShowAddModal(false); navigate('/upload'); }}
                  onManual={() => setAddMode('manual')}
                />
              ) : (
                <form id="dashboard-add-property" onSubmit={handleManualSubmit} className="space-y-4">
                  {addError && (
                    <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded text-sm">
                      {addError}
                    </div>
                  )}
                  <div className="grid grid-cols-2 gap-3">
                    <div className="col-span-2">
                      <Input name="address" required label="Address *" placeholder="123 Industrial Blvd" />
                    </div>
                    <Input name="city" required label="City *" placeholder="Ontario" />
                    <Input name="state" label="State" placeholder="CA" defaultValue="CA" maxLength={2} />
                    <Input name="zip_code" label="Zip Code" placeholder="91761" />
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Property Type</label>
                      <select
                        name="property_type"
                        className="block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 text-sm"
                      >
                        <option value="">Select type...</option>
                        <optgroup label="Industrial">
                          <option value="warehouse">Warehouse</option>
                          <option value="distribution_center">Distribution Center</option>
                          <option value="manufacturing">Manufacturing</option>
                          <option value="flex_space">Flex Space</option>
                          <option value="cold_storage">Cold Storage</option>
                          <option value="industrial">Industrial (General)</option>
                        </optgroup>
                        <optgroup label="Commercial">
                          <option value="retail">Retail</option>
                          <option value="office">Office</option>
                          <option value="mixed_use">Mixed Use</option>
                        </optgroup>
                        <optgroup label="Residential">
                          <option value="multifamily">Multifamily</option>
                          <option value="residential">Residential</option>
                        </optgroup>
                        <optgroup label="Other">
                          <option value="land">Land</option>
                          <option value="other">Other</option>
                        </optgroup>
                      </select>
                    </div>
                    <Input name="building_size" type="number" label="Building Size (SF)" placeholder="50000" />
                    <Input name="lot_size" type="number" label="Lot Size (SF)" placeholder="80000" />
                    <Input name="year_built" type="number" label="Year Built" placeholder="2005" />
                    <Input name="price" type="number" label="Price" placeholder="5000000" />
                    <Input name="cap_rate" type="number" step="0.01" label="CAP Rate (%)" placeholder="5.5" />
                    <div className="col-span-2">
                      <Input name="apn" label="APN(s)" placeholder="0110-141-01, 0110-141-02" />
                      <p className="mt-1 text-xs text-gray-500">Separate multiple APNs with commas</p>
                    </div>
                  </div>
                </form>
              )}
      </Dialog>

      {/* A5 duplicate prompt. Rendered after the Add Property modal and at z-60 so it sits on
          top of it - the modal stays open underneath so a Cancel returns the user to the form
          they already filled in rather than losing it. No documentId on this path, so
          availableActions correctly omits Attach. */}
      {duplicates && (
        <DuplicatePropertyDialog
          candidates={duplicates}
          busy={addSaving}
          onChoose={handleDuplicateChoice}
          onCancel={() => setDuplicates(null)}
        />
      )}
    </Layout>
  );
}
